import { forwardRef, useState, type JSX } from 'react';

import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupText,
  InputGroupTextarea,
} from '../../primitives/input-group';
import { LoadingAddon } from '../../primitives/loading-addon';
import { Textarea } from '../../primitives/textarea';

interface TextareaFieldProps extends React.ComponentProps<typeof Textarea> {
  button?: {
    disabled?: boolean;
    icon?: JSX.Element;
    id?: string;
    onClick: VoidFunction;
    text?: string;
  };
  counter?: {
    enable: boolean;
    hasWords?: boolean;
  };
  isLoading?: boolean;
}

const TextareaField = forwardRef<HTMLTextAreaElement, TextareaFieldProps>(
  (
    {
      counter,
      disabled = false,
      button,
      className,
      value: tmpValue,
      onChange,
      isLoading,
      ...props
    },
    ref,
  ) => {
    const [internalValue, setInternalValue] = useState('');
    const value = tmpValue === undefined ? internalValue : tmpValue;

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      if (onChange) {
        onChange(e);
      } else {
        setInternalValue(e.target.value);
      }
    };

    const currentLength = typeof value === 'string' ? value.length : 0;
    const wordsCount =
      typeof value === 'string' ? value.trim().split(/\s+/).filter(Boolean).length : 0;
    const hasCounter = Boolean(counter?.enable);
    const { icon, id, onClick: buttonClick, text } = button ?? {};
    const hasButton = typeof buttonClick === 'function';

    return (
      <InputGroup disabled={disabled || isLoading}>
        <InputGroupTextarea
          className={className}
          disabled={disabled || isLoading}
          ref={ref}
          value={value}
          onChange={handleChange}
          {...props}
        />
        {hasCounter || hasButton ? (
          <InputGroupAddon align="block-end" className="flex-wrap justify-end gap-4">
            {hasCounter ? (
              <InputGroupText>
                <p>
                  {currentLength} {currentLength === 1 ? 'carácter' : 'caracteres'}
                  {counter?.hasWords ? (
                    <>
                      <span className="mx-1">|</span>
                      {wordsCount} palabra{wordsCount === 1 ? '' : 's'}
                    </>
                  ) : null}
                </p>
              </InputGroupText>
            ) : null}
            {hasButton ? (
              <InputGroupButton
                className="flex-none"
                disabled={disabled || button?.disabled}
                id={id}
                size="xs"
                title={text ?? 'Acción'}
                variant="secondary"
                onClick={buttonClick}
              >
                {icon}
                {text}
              </InputGroupButton>
            ) : null}
          </InputGroupAddon>
        ) : null}
        {isLoading && <LoadingAddon />}
      </InputGroup>
    );
  },
);

TextareaField.displayName = 'TextareaField';

export { TextareaField };
